import React from "react";
import moment from "moment";
import FadeIn from "react-fade-in/lib/FadeIn";
import { Row, Col } from "react-flexbox-grid";
import { Nav, Card, Loader, Modal, FormSelect, Button, ButtonIcon } from "ui-kit-ck-consultant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

import ShowOptionsNGRA from "../../../components/general/ShowOptionsNGRA-v2";
import WeekGrid, {
  SOURCE_LABELS,
  SOURCE_COLORS,
} from "../../../components/planning/appointment/WeekGrid";
import ModalBookSlot from "../../../components/planning/appointment/ModalBookSlot";

import axiosConfig from "../../../utils/axiosConfig";
import filterStore from "../../../stores/filterStore";
import AuthContext from "../../../context/AuthContext";
import "../../../components/planning/appointment/Appointment.css";

const ERRORS = {
  E_AGENCY_NOT_ALLOWED: "Cette agence n'est pas dans votre périmètre.",
  E_IXPERIENCE_UNREACHABLE: "ixperience ne répond pas : le planning n'a pas pu être chargé.",
  E_IXPERIENCE_UNAUTHORIZED: "ixperience a refusé la demande (secret partagé).",
  E_IXPERIENCE_SECRET_MISSING: "Le secret partagé avec ixperience n'est pas configuré.",
  E_APPOINTMENT_UNKNOWN: "Ce rendez-vous n'existe plus.",
  E_FOLDER_UNKNOWN: "Ce dossier est introuvable.",
  E_VALIDATION: "Les informations envoyées sont incomplètes.",
};

const message = (code) => ERRORS[code] || "Une erreur est survenue.";

/**
 * Appointment planning of one agency, week by week.
 *
 * Everything shown comes from ixperience, where the three ways of booking an
 * agency slot land: the expertise-solution wizard, the no-login SMS link, and a
 * cabinet — here or on the ixperience agenda. Booking asks ixperience too: it
 * owns the push of the date to ALPHA and the RETOUR-DATE document.
 */
export default class Appointment extends React.Component {
  static contextType = AuthContext;

  constructor(props) {
    super(props);
    const saved = filterStore.getState().getPageState("planning-appointment");
    this.state = saved || {
      network: 0,
      group: 0,
      region: 0,
      agency: null,
      agencies: [],
      agencyId: null,
      monday: moment().startOf("isoWeek").format("YYYY-MM-DD"),
      data: null,
      isLoading: false,
      error: null,
      slot: null,
      moving: null,
      appointment: null,
      isCancelling: false,
    };
  }

  // The filter bar only speaks when the user touches it, and the perimeter the
  // token already carries is enough to show something: the page opens on the
  // first agency of that perimeter rather than on an empty grid.
  componentDidMount() {
    this.loadAgencies();
  }

  componentWillUnmount() {
    filterStore.getState().setPageState("planning-appointment", this.state);
  }

  handleDataChange = (data) => {
    filterStore.getState().setPageFilters("planning-appointment", data);
    this.setState(
      {
        network: data.network,
        group: data.group,
        region: data.region,
        agency: data.agencyMultiples,
      },
      this.loadAgencies
    );
  };

  loadAgencies = () => {
    const { network, group, region, agency } = this.state;
    axiosConfig("planning/appointment/get-agencies", {
      data: { network, group, region, agency },
    }).then((res) => {
      if (!res.data.success) {
        this.setState({ error: message(res.data.code) });
        return;
      }
      const agencies = res.data.data;
      // One agency at a time: the grid is that agency's opening hours.
      const agencyId =
        agencies.some((item) => Number(item.id) === Number(this.state.agencyId))
          ? this.state.agencyId
          : agencies.length
          ? agencies[0].id
          : null;
      this.setState({ agencies, agencyId, error: null }, this.loadWeek);
    });
  };

  loadWeek = () => {
    const { agencyId, monday } = this.state;
    if (!agencyId) {
      this.setState({ data: null });
      return;
    }

    this.setState({ isLoading: true, error: null }, () =>
      axiosConfig("planning/appointment/get-week", {
        data: {
          agencyId,
          startDate: monday,
          endDate: moment(monday).add(6, "days").format("YYYY-MM-DD"),
        },
      })
        .then((res) =>
          this.setState({
            isLoading: false,
            data: res.data.success ? res.data.data : null,
            error: res.data.success ? null : message(res.data.code),
          })
        )
        .catch(() => this.setState({ isLoading: false, error: message() }))
    );
  };

  moveWeek = (weeks) =>
    this.setState(
      (state) => ({ monday: moment(state.monday).add(weeks, "week").format("YYYY-MM-DD") }),
      this.loadWeek
    );

  handleCancel = () => {
    const { appointment, agencyId } = this.state;
    this.setState({ isCancelling: true }, () =>
      axiosConfig("planning/appointment/cancel", {
        data: { agencyId, folderId: appointment.folderId },
      })
        .then((res) => {
          this.setState({ isCancelling: false, appointment: null });
          if (res.data.success) this.loadWeek();
          else this.setState({ error: message(res.data.code) });
        })
        .catch(() => this.setState({ isCancelling: false, appointment: null, error: message() }))
    );
  };

  renderLegend() {
    return (
      <div className="AppointmentGrid-legend mt-10">
        {Object.keys(SOURCE_LABELS).map((source) => (
          <span key={source} className="nowrap">
            <span className={`AppointmentGrid-dot ${SOURCE_COLORS[source]}`} />
            {SOURCE_LABELS[source]}
          </span>
        ))}
        <span className="nowrap">
          <span className="AppointmentGrid-dot bg-green-light" />
          Créneau libre (cliquer pour en prendre un)
        </span>
      </div>
    );
  }

  render() {
    const { data, isLoading, monday, agencies, agencyId, slot, moving, appointment } = this.state;

    return (
      <>
        <FadeIn>
          <Nav title="Planning des rendez-vous" className="mb-10 mb-20-xl" />
          <ShowOptionsNGRA
            isChange={true}
            className="mb-10 mb-20-xl"
            onDataChange={this.handleDataChange}
            initialFilters={filterStore.getState().getPageFilters("planning-appointment")}
            isAgencyMultiples={true}
            visibilityRole={this.props.visibilityRole}
            visibilityType={this.props.visibilityType}
            preloadRole={this.props.preloadRole}
            preloadType={this.props.preloadType}
            limitUserType={this.props.limitUserType}
            limitUserRole={this.props.limitUserRole}
            allUserRole={this.props.allUserRole}
          />

          <Card className="mb-10 mb-20-xl">
            <Row>
              <Col xs={12} md={4} lg={3}>
                <FormSelect
                  title="Agence"
                  value={agencyId || ""}
                  ignore={true}
                  options={agencies.map((item) => ({ value: item.id, text: item.name }))}
                  onChange={(event) => this.setState({ agencyId: event.target.value }, this.loadWeek)}
                />
              </Col>
              <Col xs={12} md={8} lg={9}>
                <div className="AppointmentToolbar">
                  <ButtonIcon info="Semaine précédente" isBackground onClick={() => this.moveWeek(-1)}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </ButtonIcon>
                  <b className="nowrap">
                    Semaine du {moment(monday).format("DD/MM/YYYY")} au{" "}
                    {moment(monday).add(6, "days").format("DD/MM/YYYY")}
                  </b>
                  <ButtonIcon info="Semaine suivante" isBackground onClick={() => this.moveWeek(1)}>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </ButtonIcon>
                  <Button
                    className="ml-auto w-auto"
                    text="Prendre un rendez-vous"
                    green
                    small
                    disabled={!agencyId}
                    onClick={() => this.setState({ slot: {} })}
                  />
                  <Button
                    className="w-auto"
                    text="Aujourd'hui"
                    outline
                    small
                    onClick={() =>
                      this.setState(
                        { monday: moment().startOf("isoWeek").format("YYYY-MM-DD") },
                        this.loadWeek
                      )
                    }
                  />
                </div>
              </Col>
            </Row>

            {this.state.error ? <p className="red mt-10">{this.state.error}</p> : null}
            {isLoading ? <Loader /> : null}
            {!isLoading && !agencies.length ? (
              <p className="mt-10 mb-0">Aucune agence dans ce périmètre.</p>
            ) : null}

            {!isLoading && data ? (
              <div className="mt-10">
                <WeekGrid
                  days={data.days}
                  appointments={data.appointments}
                  onFreeSlot={(date, hour) => this.setState({ slot: { date, hour } })}
                  onAppointment={(item) => this.setState({ appointment: item })}
                />
                {this.renderLegend()}
              </div>
            ) : null}
          </Card>
        </FadeIn>

        {slot || moving ? (
          <ModalBookSlot
            show={true}
            agencyId={agencyId}
            agencyName={data && data.agency ? data.agency.name : null}
            agencies={agencies}
            region={this.state.region}
            agencyFilters={this.state.agency}
            date={moving ? moving.date : slot.date}
            hour={moving ? moving.hour : slot.hour}
            folder={
              moving
                ? {
                    folderId: moving.folderId,
                    reference: moving.reference,
                    kindClient: moving.kindClient,
                    nameClient: moving.nameClient,
                    licensePlate: moving.licensePlate,
                    agencyId: moving.agencyId,
                    agencyName: moving.agencyName,
                    appointmentDate: moving.date,
                    appointmentHour: moving.hour,
                  }
                : null
            }
            onClose={() => this.setState({ slot: null, moving: null })}
            onBooked={(bookedAgencyId) =>
              this.setState(
                (state) => ({
                  slot: null,
                  moving: null,
                  agencyId: state.agencies.some((item) => Number(item.id) === Number(bookedAgencyId))
                    ? bookedAgencyId
                    : state.agencyId,
                }),
                this.loadWeek
              )
            }
          />
        ) : null}

        {appointment ? (
          <Modal
            title={`Rendez-vous du ${moment(appointment.date).format("DD/MM/YYYY")} à ${appointment.hour}`}
            show={!!appointment}
            onClose={() => this.setState({ appointment: null })}
          >
            <p className="mb-0">
              <b>{appointment.reference}</b>
              <br />
              {[appointment.kindClient, appointment.nameClient].filter(Boolean).join(" ")}
              {appointment.licensePlate ? (
                <>
                  <br />
                  {appointment.licensePlate}
                </>
              ) : null}
              {appointment.sinister ? (
                <>
                  <br />
                  Sinistre n°{appointment.sinister}
                </>
              ) : null}
              <br />
              {SOURCE_LABELS[appointment.source] || appointment.source}
              {appointment.userName ? ` — ${appointment.userName}` : ""}
            </p>
            <p className="mt-10 mb-0 AppointmentGrid-hint">
              Déplacer prend un nouveau créneau à la place de celui-ci et prévient ALPHA de la
              nouvelle date. Annuler libère le créneau ; la date reste dans ALPHA jusqu'au prochain
              rendez-vous.
            </p>
            <div className="AppointmentGrid-footer AppointmentGrid-actions">
              <Button
                className="w-auto"
                text="Déplacer"
                onClick={() => this.setState({ moving: appointment, appointment: null })}
              />
              <Button
                className="w-auto"
                text={this.state.isCancelling ? "Annulation…" : "Annuler le rendez-vous"}
                red
                disabled={this.state.isCancelling}
                onClick={this.handleCancel}
              />
            </div>
          </Modal>
        ) : null}
      </>
    );
  }
}
